import React, { useContext, useEffect, useMemo, useState } from 'react';

import {
  useReferralEarnings as useReferralEarningsService,
  useReferralEarningsHistory as useReferralEarningsHistoryService,
  useReferralUsers as useReferralUsersService
} from 'services/referral';

import { useWebSocket } from 'hooks/useWebSocket';
import { useUserSelector } from 'hooks/useUser';
import { useSettings } from './useSettings';
import { useTranslation } from 'react-i18next';

const ReferralContext = React.createContext(null);

export function ReferralContextProvider({ children }) {
  const [referralSearchParts, setReferralSearchParts] = useState([]);
  const { user } = useUserSelector();
  const {
    data: referralEarnings = {},
    isLoading: isReferralEarningsLoading,
    refetch: reloadReferralEarnings
  } = useReferralEarningsService({
    enabled: !!user?.user_id && !!user?.profile_setup_completed
  });
  const {
    data: referralEarningsHistoryPages = [],
    isLoading: isReferralEarningsHistoryLoading,
    refetch: reloadReferralEarningsHistory,
    hasNextPage: hasNextPageReferralEarningsHistory,
    fetchNextPage: fetchNextPageReferralEarningsHistory
  } = useReferralEarningsHistoryService({
    enabled: !!user?.user_id && !!user?.profile_setup_completed
  });
  const {
    data: referralUsersPages = [],
    isLoading: isReferralUsersLoading,
    refetch: reloadReferralUsers,
    hasNextPage: hasNextPageReferralUsers,
    fetchNextPage: fetchNextPageReferralUsers
  } = useReferralUsersService({
    enabled: !!user?.user_id && !!user?.profile_setup_completed
  });
  const { t } = useTranslation();
  const { onDataRefreshMessage } = useWebSocket();
  const { settings } = useSettings();

  useEffect(() => {
    onDataRefreshMessage('REFERRAL_EARNINGS', reloadReferralEarnings);
    onDataRefreshMessage('REFERRAL_USERS', reloadReferralUsers);
    onDataRefreshMessage('REFERRAL_EARNINGS_HISTORY', reloadReferralEarningsHistory);
  }, [
    onDataRefreshMessage,
    reloadReferralEarningsHistory,
    reloadReferralUsers,
    reloadReferralEarnings
  ]);

  const referralEarningsHistory = useMemo(
    () => referralEarningsHistoryPages?.pages?.map(({ data }) => data)?.flat() || [],
    [referralEarningsHistoryPages]
  );
  const referralUsers = useMemo(
    () => referralUsersPages?.pages?.map(({ data }) => data)?.flat() || [],
    [referralUsersPages]
  );
  const filteredReferralEarningsHistory = useMemo(() => {
    if (!referralSearchParts?.length) {
      return referralEarningsHistory;
    }

    return referralEarningsHistory.filter(({ first_name, last_name, property_country, status }) => {
      const clientName = `${first_name} ${last_name}`.toLowerCase();
      const propertyCountry = t(property_country?.label?.toLowerCase());
      const pipeline = settings?.value?.find(({ id }) => status?.pipedrive_pipeline_id === id);
      const stage = pipeline?.stages?.find(({ id }) => status?.pipedrive_stage_id === id);
      const statusName = `${t(pipeline?.name)} - ${t(stage?.name)}`.toLowerCase();

      const countrySearchTerms = referralSearchParts.filter(({ type }) => type === 'country');
      const statusSearchTerms = referralSearchParts.filter(({ type }) => type === 'status');
      const nameSearchTerms = referralSearchParts.filter(({ type }) => type === 'name');

      const hasCountry =
        !countrySearchTerms.length ||
        countrySearchTerms.some(
          ({ label }) => propertyCountry.indexOf(label?.toLowerCase()) !== -1
        );
      const hasStatus =
        !statusSearchTerms.length ||
        statusSearchTerms.some(({ label }) => statusName.indexOf(label?.toLowerCase()) !== -1);
      const hasName =
        !nameSearchTerms.length ||
        nameSearchTerms.some(({ label }) => clientName.indexOf(label?.toLowerCase()) !== -1);

      return hasCountry && hasStatus && hasName;
    });
  }, [referralEarningsHistory, settings, t, referralSearchParts]);

  return (
    <ReferralContext.Provider
      value={{
        referralEarnings,
        isReferralEarningsLoading,
        reloadReferralEarnings,
        referralEarningsHistory,
        isReferralEarningsHistoryLoading,
        reloadReferralEarningsHistory,
        referralUsers,
        isReferralUsersLoading,
        reloadReferralUsers,
        hasNextPageReferralEarningsHistory,
        fetchNextPageReferralEarningsHistory,
        hasNextPageReferralUsers,
        fetchNextPageReferralUsers,
        filteredReferralEarningsHistory,
        referralSearchParts,
        setReferralSearchParts
      }}
    >
      {children}
    </ReferralContext.Provider>
  );
}

export function useReferral() {
  const context = useContext(ReferralContext);

  if (!context) {
    throw new Error('useReferral must be used within a ReferralContextProvider');
  }

  return context;
}
